// BOLUS_NOTES-6 (2026-09-16): the glucose_update stream is now PRIVATE on
// Nexus (apps.metadata.streams → private:true), so subscribing needs a
// Bearer JWT. Browser EventSource cannot set headers, so this is a minimal
// fetch()-based SSE reader with the same event/reconnect semantics we used.
//
// Usage:
//   const stop = subscribeSse(url, { onEvent: (name, data) => …, onError });
//   … stop();
export interface SseHandlers {
  onEvent: (eventName: string, data: string) => void;
  onError?: (err: unknown) => void;
  onOpen?: () => void;
}

function currentAccessToken(): string | null {
  try {
    const raw = localStorage.getItem('bolusnotes_auth_tokens');
    if (!raw) return null;
    return JSON.parse(raw)?.accessToken || null;
  } catch {
    return null;
  }
}

export function subscribeSse(url: string, handlers: SseHandlers): () => void {
  let stopped = false;
  let controller: AbortController | null = null;
  let backoffMs = 1000;

  const run = async () => {
    while (!stopped) {
      controller = new AbortController();
      try {
        const token = currentAccessToken();
        const res = await fetch(url, {
          headers: {
            Accept: 'text/event-stream',
            'x-nexus-app-key': process.env.REACT_APP_BOLUSNOTES_KEY || '',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
          cache: 'no-store',
        });
        if (!res.ok || !res.body) {
          throw new Error(`SSE HTTP ${res.status}`);
        }
        backoffMs = 1000;
        handlers.onOpen?.();
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        let eventName = 'message';
        let dataLines: string[] = [];
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let nl: number;
          while ((nl = buf.indexOf('\n')) >= 0) {
            const line = buf.slice(0, nl).replace(/\r$/, '');
            buf = buf.slice(nl + 1);
            if (line === '') {
              if (dataLines.length) handlers.onEvent(eventName, dataLines.join('\n'));
              eventName = 'message';
              dataLines = [];
            } else if (line.startsWith(':')) {
              // heartbeat / comment
            } else if (line.startsWith('event:')) {
              eventName = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              dataLines.push(line.slice(5).replace(/^ /, ''));
            }
          }
        }
      } catch (err) {
        if (stopped) break;
        handlers.onError?.(err);
      }
      if (stopped) break;
      await new Promise((r) => setTimeout(r, backoffMs));
      backoffMs = Math.min(backoffMs * 2, 30000);
    }
  };
  void run();

  return () => {
    stopped = true;
    controller?.abort();
  };
}
